import {
  EmailProvider,
  EmailRequestType,
  UnifiedSendEmailRequest,
  UserCommunication,
} from '@this+that/models';
import axios from 'axios';
import log from 'loglevel';

import { DraftAccount, resolveDraftAccount } from './resolveEmailAccount';
import { sendEmails } from '../../../services/unifiedEmailAction.service';
import { useGetPaginatedUserCommunicationsByThread } from '../../../services/userCommunications.service';
import {
  useIntegrationsStore,
  useUserCommunicationsStore,
} from '../../../store';
import { useCommunicationIntentsStore } from '../../../store/Inbox/CommunicationIntents';
import { DRAFT_NOT_FOUND_ERROR_PATTERNS } from '../../../utils/constants/draftErrors';
import {
  buildThreadRestoreUserCommunication,
  isDraft,
} from '../../../utils/userCommunicationHelpers';

// A thread rarely has enough history for pagination to matter here — this
// just needs to be generous enough to reach the newest non-draft message.
const THREAD_RESTORE_FETCH_LIMIT = 50;

export const isDraftNotFoundError = (error: unknown): boolean => {
  const message = axios.isAxiosError(error)
    ? (error.response?.data?.message ?? '')
    : error instanceof Error
      ? error.message
      : '';

  const normalizedMessage = message.toLowerCase();

  return DRAFT_NOT_FOUND_ERROR_PATTERNS.some((pattern) =>
    normalizedMessage.includes(pattern.toLowerCase()),
  );
};

const findDraftThreadCarrier = (
  union: UserCommunication[],
  draftId: string,
): UserCommunication | undefined =>
  union.find((uc) => uc.threadMessages?.some((msg) => msg.draftId === draftId));

const resolveUserCommunicationFromSnapshot = (
  union: UserCommunication[],
  draftId: string,
): UserCommunication | undefined =>
  union.find((uc) => uc.draftId === draftId) ??
  union.find((uc) => uc.messageId === draftId) ??
  findDraftThreadCarrier(union, draftId);

export interface DeleteDraftOptions {
  skipThreadRestore?: boolean;
  removeFromStore?: boolean;
  fallbackEmailAddress?: string;
  accountOverride?: DraftAccount;
}

export const useDeleteDraft = () => {
  const {
    primary,
    promotions,
    social,
    updates,
    forums,
    draft,
    sent,
    spam,
    trash,
    labels,
    all,
    removeAndInsertUserCommunication,
    insertUserCommunicationIntoSlices,
    deleteDraft: deleteDraftFromStore,
    getUserCommunicationByDraftId,
    getUserCommunicationByMessageId,
    getUserCommunicationByThreadMessageId,
  } = useUserCommunicationsStore();
  const { currentIntegrationAccount, integrationAccounts } =
    useIntegrationsStore();
  const { getPaginatedUserCommunicationsByThread } =
    useGetPaginatedUserCommunicationsByThread();

  // No non-draft message is known locally for this thread — either it was
  // never fetched, or every locally-known copy only carried other drafts
  // (see buildThreadRestoreUserCommunication). Fetches the thread fresh and
  // promotes its newest non-draft message to head, same shape as the local
  // restore. A thread with no non-draft message at all (a brand-new compose)
  // has nothing to restore, so the caller skips this entirely. `sliceKeys`,
  // when known, are the slices handleDeleteDraft found the draft row in —
  // restoring into exactly those instead of recomputing membership from the
  // fetched head's own labels (see STORE.md's "restore in place" rule).
  const restoreThreadFromServer = async (
    threadMessageId: string,
    sliceKeys?: string[],
  ) => {
    const { userCommunications } = await getPaginatedUserCommunicationsByThread(
      { threadMessageId, limit: THREAD_RESTORE_FETCH_LIMIT },
    );
    const nonDraftMessages = (userCommunications ?? [])
      .filter((msg) => !isDraft(msg))
      .sort((a, b) => b.createdAt - a.createdAt);

    if (nonDraftMessages.length === 0) {
      return;
    }

    const [head, ...rest] = nonDraftMessages;
    const { communication: reconciled } = useCommunicationIntentsStore
      .getState()
      .reconcileCommunication({ ...head, threadMessages: rest });

    if (sliceKeys?.length) {
      insertUserCommunicationIntoSlices(reconciled, sliceKeys);
    } else {
      removeAndInsertUserCommunication(reconciled);
    }
  };

  const restoreThreadAfterDelete = async (
    userCommunication: UserCommunication,
    draftId: string,
  ) => {
    const { threadMessageId } = userCommunication;

    // The store-level handleDeleteDraft, which always runs first in the
    // production call path, already restored this thread in place — a row
    // found here that isn't still the draft itself (draftId no longer
    // matches, and it isn't DRAFT-labeled) is that restoration, not a stale
    // read. Re-restoring from this hook's own snapshot on top of it would
    // route the row through calculateViews instead, undoing the in-place fix.
    const existingByThread = threadMessageId
      ? getUserCommunicationByThreadMessageId(threadMessageId)
      : undefined;
    if (
      existingByThread &&
      existingByThread.draftId !== draftId &&
      !isDraft(existingByThread)
    ) {
      return;
    }

    const sliceKeys =
      useUserCommunicationsStore.getState().lastDeletedDraftSliceKeys[draftId];

    const restored = buildThreadRestoreUserCommunication(
      userCommunication,
      draftId,
    );

    if (restored) {
      const { communication: reconciled } = useCommunicationIntentsStore
        .getState()
        .reconcileCommunication(restored);

      if (sliceKeys?.length) {
        insertUserCommunicationIntoSlices(reconciled, sliceKeys);
      } else {
        removeAndInsertUserCommunication(reconciled);
      }

      return;
    }

    const { threadMessages } = userCommunication;
    if (!threadMessageId || !threadMessages?.length) {
      return;
    }

    await restoreThreadFromServer(threadMessageId, sliceKeys);
  };

  const findDraftSnapshot = (
    draftId: string,
  ): UserCommunication | undefined => {
    const union = [
      ...primary,
      ...promotions,
      ...social,
      ...updates,
      ...forums,
      ...draft,
      ...sent,
      ...spam,
      ...trash,
      ...all,
      ...Object.values(labels).flat(),
    ];

    return (
      resolveUserCommunicationFromSnapshot(union, draftId) ??
      getUserCommunicationByDraftId(draftId) ??
      getUserCommunicationByMessageId(draftId)
    );
  };

  const resolveDeleteAccount = (
    userCommunication: UserCommunication | undefined,
    options: DeleteDraftOptions | undefined,
  ): DraftAccount | null => {
    if (options?.accountOverride) {
      return options.accountOverride;
    }
    if (!currentIntegrationAccount) {
      return null;
    }
    return resolveDraftAccount(
      userCommunication?.userEmailAddress ??
        options?.fallbackEmailAddress ??
        undefined,
      currentIntegrationAccount,
      integrationAccounts,
    );
  };

  const deleteOnProvider = async (draftId: string, account: DraftAccount) => {
    const payload = {
      providerAccount: {
        provider: account.platform as EmailProvider,
        accountId: account.accountId,
      },
      reqType: EmailRequestType.DELETE_DRAFT,
      draftId,
    } satisfies UnifiedSendEmailRequest;

    try {
      await sendEmails(payload);
    } catch (error) {
      if (!isDraftNotFoundError(error)) {
        throw error;
      }
      log.info(
        `Draft already gone from the provider, treating as deleted (draftId: ${draftId})`,
        error,
      );
    }
  };

  const finalizeConfirmedDelete = async (
    draftId: string,
    userCommunication: UserCommunication | undefined,
    options: DeleteDraftOptions | undefined,
  ) => {
    if (options?.removeFromStore) {
      deleteDraftFromStore(draftId);
    }

    if (!userCommunication || options?.skipThreadRestore) {
      return;
    }

    try {
      await restoreThreadAfterDelete(userCommunication, draftId);
    } catch (error) {
      log.error(
        `Draft deleted but the thread restore failed (draftId: ${draftId}):`,
        error,
      );
    }
  };

  const deleteDraft = async (
    draftId: string,
    options?: DeleteDraftOptions,
  ): Promise<boolean> => {
    try {
      if (!draftId) {
        log.error('Draft ID is required to delete draft');
        return false;
      }

      const userCommunication = findDraftSnapshot(draftId);

      if (
        !userCommunication &&
        !options?.fallbackEmailAddress &&
        !options?.accountOverride
      ) {
        log.error(`UserCommunication not found for draftId: ${draftId}`);
        return false;
      }

      const account = resolveDeleteAccount(userCommunication, options);

      if (!account) {
        log.error(
          `Missing platform or email to delete draft (draftId: ${draftId})`,
        );
        return false;
      }

      await deleteOnProvider(draftId, account);
      await finalizeConfirmedDelete(draftId, userCommunication, options);
      return true;
    } catch (error) {
      log.error('Failed to delete draft:', error);
      return false;
    }
  };

  return {
    deleteDraft,
  };
};
